'use client';

import { useState, useEffect, useRef } from 'react';
import { Zap, Download, Upload, Clock, Wifi } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { speedTestContent } from '../../data/speed-test-content';

export default function SpeedTest() {
    const [isTesting, setIsTesting] = useState(false);
    const [downloadSpeed, setDownloadSpeed] = useState<number | null>(null);
    const [uploadSpeed, setUploadSpeed] = useState<number | null>(null);
    const [ping, setPing] = useState<number | null>(null);
    const [testProgress, setTestProgress] = useState(0);
    const [testPhase, setTestPhase] = useState<'idle' | 'ping' | 'download' | 'upload' | 'complete'>('idle');

    const testFileUrl = '/test-file.dat'; // We'll create this file later
    const testFileSize = 1 * 1024 * 1024; // 1MB test file

    const formatSpeed = (bytesPerSecond: number | null): string => {
        if (bytesPerSecond === null) return '--';
        if (bytesPerSecond < 1024) return `${bytesPerSecond.toFixed(2)} B/s`;
        if (bytesPerSecond < 1024 * 1024) return `${(bytesPerSecond / 1024).toFixed(2)} KB/s`;
        return `${(bytesPerSecond / (1024 * 1024)).toFixed(2)} MB/s`;
    };

    const formatPing = (pingMs: number | null): string => {
        if (pingMs === null) return '--';
        return `${pingMs.toFixed(0)} ms`;
    };

    const measurePing = async (): Promise<number> => {
        const start = performance.now();
        try {
            await fetch('/api/ping', { cache: 'no-store' });
            return performance.now() - start;
        } catch {
            return 0;
        }
    };

    const measureDownloadSpeed = async (): Promise<number> => {
        const startTime = performance.now();
        try {
            const response = await fetch(`${testFileUrl}?size=${testFileSize}&t=${Date.now()}`, {
                cache: 'no-store',
            });
            const blob = await response.blob();
            const endTime = performance.now();

            if (blob.size === 0) throw new Error('Empty response');

            const duration = (endTime - startTime) / 1000; // in seconds
            return (testFileSize / duration); // bytes per second
        } catch (error) {
            console.error('Download test failed:', error);
            return 0;
        }
    };

    const measureUploadSpeed = async (): Promise<number> => {
        const blob = new Blob([new Uint8Array(testFileSize)], { type: 'application/octet-stream' });
        const formData = new FormData();
        formData.append('file', blob, 'speedtest.bin');

        const startTime = performance.now();
        try {
            const response = await fetch('/api/upload-test', {
                method: 'POST',
                body: formData,
                cache: 'no-store',
            });

            if (!response.ok) throw new Error('Upload failed');

            const endTime = performance.now();
            const duration = (endTime - startTime) / 1000; // in seconds
            return (testFileSize / duration); // bytes per second
        } catch (error) {
            console.error('Upload test failed:', error);
            return 0;
        }
    };

    const runSpeedTest = async () => {
        if (isTesting) return;

        setIsTesting(true);
        setTestProgress(0);
        setTestPhase('ping');

        try {
            // Test Ping
            const pingTime = await measurePing();
            setPing(pingTime);
            setTestProgress(20);

            // Test Download Speed
            setTestPhase('download');
            const downloadSpeed = await measureDownloadSpeed();
            setDownloadSpeed(downloadSpeed);
            setTestProgress(60);

            // Test Upload Speed
            setTestPhase('upload');
            const uploadSpeed = await measureUploadSpeed();
            setUploadSpeed(uploadSpeed);

            setTestProgress(100);
            setTestPhase('complete');
            toast.success('Speed test completed!');
        } catch (error) {
            console.error('Speed test failed:', error);
            toast.error('Speed test failed. Please try again.');
        } finally {
            setIsTesting(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="text-center mb-8">
                <ToolPageIntro title="Speed Test - Free Online Internet Speed Test Tool">
                Test your internet connection speed with our free online speed test. Measure download/upload speeds, latency, and performance.
            </ToolPageIntro>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
                <div className="flex justify-center mb-8">
                    <button
                        onClick={runSpeedTest}
                        disabled={isTesting}
                        className={`flex items-center px-8 py-4 rounded-full text-white font-bold text-lg transition-all ${isTesting ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                    >
                        <Zap className="mr-2" />
                        {isTesting ? 'Testing...' : 'Start Test'}
                    </button>
                </div>

                {isTesting && (
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mb-8">
                        <div
                            className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                            style={{ width: `${testProgress}%` }}
                        ></div>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-gray-50 p-6 rounded-lg text-center">
                        <div className="flex justify-center mb-2">
                            <Clock className="text-blue-600 h-8 w-8" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-1">Ping</h3>
                        <p className="text-2xl font-bold text-gray-900">{formatPing(ping)}</p>
                        <p className="text-sm text-gray-500 mt-1">
                            {testPhase === 'ping' ? 'Testing...' : 'Latency'}
                        </p>
                    </div>

                    <div className="bg-gray-50 p-6 rounded-lg text-center">
                        <div className="flex justify-center mb-2">
                            <Download className="text-green-600 h-8 w-8" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-1">Download</h3>
                        <p className="text-2xl font-bold text-gray-900">{formatSpeed(downloadSpeed)}</p>
                        <p className="text-sm text-gray-500 mt-1">
                            {testPhase === 'download' ? 'Testing...' : 'Mbps'}
                        </p>
                    </div>

                    <div className="bg-gray-50 p-6 rounded-lg text-center">
                        <div className="flex justify-center mb-2">
                            <Upload className="text-purple-600 h-8 w-8" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-1">Upload</h3>
                        <p className="text-2xl font-bold text-gray-900">{formatSpeed(uploadSpeed)}</p>
                        <p className="text-sm text-gray-500 mt-1">
                            {testPhase === 'upload' ? 'Testing...' : 'Mbps'}
                        </p>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">How It Works</h2>
                <div className="space-y-4">
                    <div className="flex items-start">
                        <div className="flex-shrink-0 bg-blue-100 p-2 rounded-full mr-3">
                            <Wifi className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                            <h3 className="font-medium text-gray-900">Ping Test</h3>
                            <p className="text-gray-600 text-sm">
                                Measures the time it takes for a small data packet to travel to our server and back.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <div className="flex-shrink-0 bg-green-100 p-2 rounded-full mr-3">
                            <Download className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                            <h3 className="font-medium text-gray-900">Download Speed</h3>
                            <p className="text-gray-600 text-sm">
                                Measures how fast data can be downloaded from the internet to your device.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <div className="flex-shrink-0 bg-purple-100 p-2 rounded-full mr-3">
                            <Upload className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                            <h3 className="font-medium text-gray-900">Upload Speed</h3>
                            <p className="text-gray-600 text-sm">
                                Measures how fast data can be uploaded from your device to the internet.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
                    <div className="flex justify-center mb-8">
                        <button
                            onClick={runSpeedTest}
                            disabled={isTesting}
                            className={`flex items-center px-8 py-4 rounded-full text-white font-bold text-lg transition-all ${isTesting ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'
                                }`}
                        >
                            <Zap className="mr-2" />
                            {isTesting ? 'Testing...' : 'Start Test'}
                        </button>
                    </div>

                    {isTesting && (
                        <div className="w-full bg-gray-200 rounded-full h-2.5 mb-8">
                            <div
                                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                                style={{ width: `${testProgress}%` }}
                            ></div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-gray-50 p-6 rounded-lg text-center">
                            <div className="flex justify-center mb-2">
                                <Clock className="text-blue-600 h-8 w-8" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-1">Ping</h3>
                            <p className="text-2xl font-bold text-gray-900">{formatPing(ping)}</p>
                            <p className="text-sm text-gray-500 mt-1">
                                {testPhase === 'ping' ? 'Testing...' : 'Latency'}
                            </p>
                        </div>

                        <div className="bg-gray-50 p-6 rounded-lg text-center">
                            <div className="flex justify-center mb-2">
                                <Download className="text-green-600 h-8 w-8" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-1">Download</h3>
                            <p className="text-2xl font-bold text-gray-900">{formatSpeed(downloadSpeed)}</p>
                            <p className="text-sm text-gray-500 mt-1">
                                {testPhase === 'download' ? 'Testing...' : 'Mbps'}
                            </p>
                        </div>

                        <div className="bg-gray-50 p-6 rounded-lg text-center">
                            <div className="flex justify-center mb-2">
                                <Upload className="text-purple-600 h-8 w-8" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-1">Upload</h3>
                            <p className="text-2xl font-bold text-gray-900">{formatSpeed(uploadSpeed)}</p>
                            <p className="text-sm text-gray-500 mt-1">
                                {testPhase === 'upload' ? 'Testing...' : 'Mbps'}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-lg p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">How It Works</h2>
                    <div className="space-y-4">
                        <div className="flex items-start">
                            <div className="flex-shrink-0 bg-blue-100 p-2 rounded-full mr-3">
                                <Wifi className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                                <h3 className="font-medium text-gray-900">Ping Test</h3>
                                <p className="text-gray-600 text-sm">
                                    Measures the time it takes for a small data packet to travel to our server and back.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <div className="flex-shrink-0 bg-green-100 p-2 rounded-full mr-3">
                                <Download className="h-5 w-5 text-green-600" />
                            </div>
                            <div>
                                <h3 className="font-medium text-gray-900">Download Speed</h3>
                                <p className="text-gray-600 text-sm">
                                    Measures how fast data can be downloaded from the internet to your device.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <div className="flex-shrink-0 bg-purple-100 p-2 rounded-full mr-3">
                                <Upload className="h-5 w-5 text-purple-600" />
                            </div>
                            <div>
                                <h3 className="font-medium text-gray-900">Upload Speed</h3>
                                <p className="text-gray-600 text-sm">
                                    Measures how fast data can be uploaded from your device to the internet.
                                </p>
                            </div>
                        </div>

                        <div className="bg-gray-50 p-6 rounded-lg text-center">
                            <div className="flex justify-center mb-2">
                                <Download className="text-green-600 h-8 w-8" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-1">Download</h3>
                            <p className="text-2xl font-bold text-gray-900">{formatSpeed(downloadSpeed)}</p>
                            <p className="text-sm text-gray-500 mt-1">
                                {testPhase === 'download' ? 'Testing...' : 'Mbps'}
                            </p>
                        </div>

                        <div className="bg-gray-50 p-6 rounded-lg text-center">
                            <div className="flex justify-center mb-2">
                                <Upload className="text-purple-600 h-8 w-8" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-1">Upload</h3>
                            <p className="text-2xl font-bold text-gray-900">{formatSpeed(uploadSpeed)}</p>
                            <p className="text-sm text-gray-500 mt-1">
                                {testPhase === 'upload' ? 'Testing...' : 'Mbps'}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-lg p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">How It Works</h2>
                    <div className="space-y-4">
                        <div className="flex items-start">
                            <div className="flex-shrink-0 bg-blue-100 p-2 rounded-full mr-3">
                                <Wifi className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                                <h3 className="font-medium text-gray-900">Ping Test</h3>
                                <p className="text-gray-600 text-sm">
                                    Measures the time it takes for a small data packet to travel to our server and back.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <div className="flex-shrink-0 bg-green-100 p-2 rounded-full mr-3">
                                <Download className="h-5 w-5 text-green-600" />
                            </div>
                            <div>
                                <h3 className="font-medium text-gray-900">Download Speed</h3>
                                <p className="text-gray-600 text-sm">
                                    Measures how fast data can be downloaded from the internet to your device.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <div className="flex-shrink-0 bg-purple-100 p-2 rounded-full mr-3">
                                <Upload className="h-5 w-5 text-purple-600" />
                            </div>
                            <div>
                                <h3 className="font-medium text-gray-900">Upload Speed</h3>
                                <p className="text-gray-600 text-sm">
                                    Measures how fast data can be uploaded from your device to the internet.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Informational Content */}
                <div className="mt-12">
                    <InformationalContent
                        title={speedTestContent.title}
                        description={speedTestContent.description}
                        content={speedTestContent.content}
                        faqs={speedTestContent.faqs}
                        relatedTools={speedTestContent.relatedTools}
                    />
                </div>
            </div>
        </div>
    );
}
